import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Search, X, User } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * Compact picker for @mentions / required_from. Restricted to
 * kind=internal_user contacts that have a linked_user_id (i.e. real users
 * with workspace access to this task). Returns user_ids (not contact_ids)
 * because backend keys off user identity.
 */
export default function MentionPicker({
  value = [],           // array of user_ids
  onChange,
  excludeUserIds = [],  // e.g. current user's own id
  label,                // "Ko treba da odgovori?" / etc.
  placeholder = "Traži interne korisnike…",
  testidPrefix = "mention",
  single = false,
}) {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/contacts?kind=internal_user")
      .then(({ data }) => setContacts((data || []).filter((c) => c.linked_user_id)))
      .catch(() => setContacts([]));
  }, []);

  const byUserId = useMemo(() => {
    const m = {};
    for (const c of contacts) m[c.linked_user_id] = c;
    return m;
  }, [contacts]);

  const available = useMemo(() => {
    const ex = new Set(excludeUserIds);
    return contacts
      .filter((c) => !ex.has(c.linked_user_id))
      .filter((c) => !value.includes(c.linked_user_id))
      .filter((c) => !q.trim() || c.name.toLowerCase().includes(q.trim().toLowerCase()));
  }, [contacts, excludeUserIds, value, q]);

  const add = (uid) => {
    if (single) {
      onChange?.([uid]);
      setOpen(false);
    } else if (!value.includes(uid)) {
      onChange?.([...value, uid]);
    }
    setQ("");
  };

  const remove = (uid) => onChange?.(value.filter((u) => u !== uid));

  return (
    <div className="space-y-1.5" data-testid={`${testidPrefix}-container`}>
      {label && (
        <div className="text-xs uppercase tracking-wider text-ink-muted font-medium">
          {label}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5 items-center">
        {value.map((uid) => {
          const c = byUserId[uid];
          return (
            <span
              key={uid}
              data-testid={`${testidPrefix}-selected-${uid}`}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-forest/8 text-forest border border-forest/25 rounded-md"
            >
              <User className="w-3 h-3" />
              {c?.name || "korisnik"}
              <button
                type="button"
                onClick={() => remove(uid)}
                className="hover:bg-forest/15 rounded-sm ml-0.5"
                data-testid={`${testidPrefix}-remove-${uid}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}
        {(!single || value.length === 0) && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid={`${testidPrefix}-open-btn`}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-dashed border-line text-ink-muted hover:bg-surface-secondary rounded-md"
          >
            <User className="w-3 h-3" />
            {value.length ? "Dodaj još" : "Izaberi osobu"}
          </button>
        )}
      </div>

      {open && (
        <div className="border border-line rounded-md bg-white p-2 space-y-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-ink-muted" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder}
              className="pl-7 h-8 text-sm"
              data-testid={`${testidPrefix}-search`}
            />
          </div>
          <div className="max-h-40 overflow-y-auto divide-y divide-line">
            {available.length === 0 && (
              <div className="text-xs text-ink-muted px-2 py-1.5">Nema više dostupnih.</div>
            )}
            {available.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => add(c.linked_user_id)}
                data-testid={`${testidPrefix}-pick-${c.linked_user_id}`}
                className="w-full text-left text-sm px-2 py-1.5 hover:bg-surface-secondary flex items-center gap-2"
              >
                <User className="w-3.5 h-3.5 text-ink-muted" />
                {c.name}
                {c.role_title && (
                  <span className="text-xs text-ink-muted"> · {c.role_title}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
